/**
 * Command-zone slot rules for the MTG add-card form.
 *
 * Kept free of imports so `scripts/command-zone-slots.test.mjs` can load it
 * directly. This decision has regressed twice — a native form reset snapped
 * the <select> to its first option, then a reset ran against a command zone
 * the refetch had not filled yet — and both escaped tsc and eslint because
 * the damage is behavioural, not typed.
 */

/**
 * Keyed by `MagicFormatEnum` members, which are also the API's wire values.
 * Kept as literals rather than an import so this module stays loadable by
 * `scripts/command-zone-slots.test.mjs`; that test asserts the enum still
 * matches.
 *
 * Commander fits a Partner pair and Oathbreaker adds a Signature Spell; Brawl
 * never takes a second command-zone card. Every other format has no command
 * zone at all, so it seats nothing.
 */
const SLOTS_BY_FORMAT = new Map<string, number>([
  ["commander", 2],
  ["oathbreaker", 2],
  ["brawl", 1],
]);

export function commandZoneSlots(format: string): number {
  return SLOTS_BY_FORMAT.get(format) ?? 0;
}

export interface CommandZoneDecision<TLocation> {
  offerCommander: boolean;
  nextLocation?: TLocation;
  disabled?: boolean;
}

export interface CommandZoneInput<TLocation> {
  commanderCount: number;
  slots: number;
  currentLocation: TLocation;
  commanderLocation: TLocation;
  mainboardLocation: TLocation;
}

/**
 * The API remains the authority on whether a specific pair is legal — this
 * only decides whether the slot is worth offering, and where to send the
 * form once it is gone.
 */
export function commandZoneDecision<TLocation>({
  commanderCount,
  slots,
  currentLocation,
  commanderLocation,
  mainboardLocation,
}: CommandZoneInput<TLocation>): CommandZoneDecision<TLocation> {
  if (commanderCount >= slots) {
    // Only correct a select that is still pointing at the slot that just
    // closed; forcing Mainboard unconditionally would fight the user's own
    // choice of Sideboard or Considering on the next add.
    return {
      offerCommander: false,
      nextLocation:
        currentLocation === commanderLocation ? mainboardLocation : undefined,
    };
  }
  if (commanderCount > 0) {
    // Room for a second command-zone card, but the user has to opt into it:
    // a forced location here is what sticks and auto-submits the next card
    // into the command zone.
    return { offerCommander: true };
  }
  return {
    offerCommander: true,
    nextLocation: commanderLocation,
    disabled: true,
  };
}
